import {
  Grid3X3,
  Laugh,
  Moon,
  Music,
  Tent,
  Theater,
  Trophy,
} from "lucide-react";
import { data as categoryData } from "../../CategorySection/DataCategory";

/** Sidebar + top-level categories on /tickets */
export const TICKETS_SIDEBAR_CATEGORIES = [
  { name: "All", icon: Grid3X3, categoryId: null },
  { name: "Concerts", icon: Music, categoryId: 54 },
  { name: "Sports", icon: Trophy, categoryId: 1 },
  { name: "Theater", icon: Theater, categoryId: 68 },
  { name: "Comedy", icon: Laugh, categoryId: 55 },
  { name: "Festivals", icon: Tent, categoryId: 73 },
  { name: "Movies", icon: Moon, categoryId: 138 },
];

const CATALOG = (() => {
  const byId = new Map();
  const byName = new Map();

  const add = (id, name, parentName = null) => {
    if (id == null || !name) return;
    const entry = { id: Number(id), name, parentName };
    byId.set(Number(id), entry);
    byName.set(normalizeCategoryName(name), entry);
  };

  for (const cat of categoryData || []) {
    add(cat.id, cat.name);
    for (const sub of cat.subCategory || []) {
      add(sub.id, sub.name, cat.name);
    }
  }

  for (const cat of TICKETS_SIDEBAR_CATEGORIES) {
    if (cat.categoryId != null) add(cat.categoryId, cat.name);
  }

  return { byId, byName };
})();

export function normalizeCategoryName(name) {
  return (name || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/-/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function findCategoryById(id) {
  const n = Number(id);
  if (!Number.isFinite(n)) return null;
  return CATALOG.byId.get(n) || null;
}

function compactCategoryName(name) {
  return normalizeCategoryName(name).replace(/\s/g, "");
}

export function findCategoryByName(name) {
  const key = normalizeCategoryName(name);
  if (!key) return null;
  if (CATALOG.byName.has(key)) return CATALOG.byName.get(key);

  const compactKey = compactCategoryName(name);
  for (const [catalogKey, entry] of CATALOG.byName.entries()) {
    if (catalogKey === key || catalogKey.includes(key) || key.includes(catalogKey)) {
      return entry;
    }
    if (compactKey && compactCategoryName(entry.name) === compactKey) {
      return entry;
    }
  }
  return null;
}

/** Resolve `?category=` and/or `?categoryId=` from the tickets URL. */
export function resolveTicketsCategoryFromSearchParams(category, categoryId) {
  if (categoryId != null && String(categoryId).trim() !== "") {
    const byId = findCategoryById(categoryId);
    if (byId) return byId;
  }

  if (category != null && String(category).trim() !== "") {
    const decoded = decodeURIComponent(String(category).trim());
    if (/^\d+$/.test(decoded)) {
      const byNumericName = findCategoryById(decoded);
      if (byNumericName) return byNumericName;
    }
    const byName = findCategoryByName(decoded);
    if (byName) return byName;
  }

  return null;
}

export function buildTicketsCategorySearch({ id, name }) {
  const params = new URLSearchParams();
  if (name) params.set("category", name);
  if (id != null) params.set("categoryId", String(id));
  const q = params.toString();
  return q ? `/tickets?${q}` : "/tickets";
}

/** Old route: `/tickets/category=59` or `/tickets/:name` */
export function legacyTicketsCategoryPathToUrl(routeName) {
  if (!routeName) return "/tickets";
  if (routeName.slice(0, 8) === "category") {
    const id = routeName.split("=")[1];
    const found = findCategoryById(id);
    if (found) return buildTicketsCategorySearch(found);
    return `/tickets?categoryId=${encodeURIComponent(id)}`;
  }
  const found = findCategoryByName(routeName);
  if (found) return buildTicketsCategorySearch(found);
  return `/tickets?category=${encodeURIComponent(routeName)}`;
}

export const TICKETS_CATEGORY_UNAVAILABLE = new Set([
  "Theme Park",
  "Moiowdjqwihdowiqvies",
]);
